import StoreCheckout from 'GlobalStores/Checkout/StoreCheckout'
import StoreCountry from 'GlobalStores/Cores/County/StoreCountry'
import StoreLoader from 'GlobalStores/Cores/Elements/StoreLoader'
import StoreFeelGreat from 'GlobalStores/FeelGreat/StoreFeelGreat'
import StoreFormat from 'GlobalStores/Format/StoreFormat'
import StoreReferral from 'GlobalStores/Referral/StoreReferral'
import StoreReferralUae from 'Components/Pages/Referral/Uae/Stores/StoreReferral'
import StoreProducts from 'GlobalStores/StoreProducts'
import StoreAuth from 'GlobalStores/User/StoreAuth'
import StoreUser from 'GlobalStores/User/StoreUser'
import { get } from 'lodash'
import { runInAction } from 'mobx'
import { GetFeelGreatProduct } from 'Services/FeelGreat/useFeelGreat'
import { Customers } from './Customers/Customers'
import { FeelGreatAPI } from 'Services/FeelGreat/APIs'
// import { FormatAPIs } from 'Services/FeelGreat/FormatAPIs'
import { applySessionToForm, formKeyByFormatKey } from 'Components/Pages/Referral/Uae/Helpers/checkoutForm'
import { ReferralUaeAPIs } from 'Components/Pages/Referral/Uae/Services/ReferralUaeAPIs'

// shipment_packs is missing whenever the format call did not land — a single failed request used to
// turn that into `JSON.parse(undefined)`, which rejected out of Initialize and left the checkout page
// waiting forever.
const parseShipmentPacks = raw => {
    if (!raw) {
        return {}
    }

    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (error) {
        return {}
    }
}

export async function Initialize() {
    runInAction(() => (StoreCheckout.checkoutPreparing = true))
    const qs = new URLSearchParams(window.location.search)

    if (qs.get('uuid')) {
        // ---> Get UUID
        // ---> Check ว่า data มาจาก login หรือ referral id
        // ---> ถ้า login เริ่มใหม่ --> Redirect กลับไปที่ / (root) เอา uuid
        // ---> ถ้า referral ---> Get customer data **
        // ---> Get Feelgreat with ba status
        // ---> Init data จาก Format API ใส่ MOBX

        if (StoreCountry.Country3() && StoreCheckout.CheckOutInstance()) {
            if (StoreCheckout.GetUuid().length === 0) {
                const settingResponse = await GetFeelGreatProduct()
                StoreLoader.InitAfterLoadSetting(settingResponse.data)
                // The session is not usable until SetData has copied it onto the checkout form, so the
                // format-API flag stays raised across both steps. Lowering it after GetUuid alone let
                // pages act on a half-loaded session — the guest-password guard read a form that
                // SetData had not filled yet, and StoreCheckout.Init then rebuilt the form under it.
                // It is lowered in a finally because a flag left raised leaves the page blank forever.
                try {
                    await GetUuid(qs.get('uuid'), StoreCountry.Country3(), true)
                    await SetData()
                } finally {
                    StoreLoader.SetLoadFormatApi(false)
                }
            }
        }
    } else {
        if (StoreCountry.Country3() && StoreLoader.IsMainApiLoaded('settings') === false) {
            const baStatus = StoreAuth.userStatus || null
            const [settingResponse, subscriptionConfigResponse] = await Promise.all([GetFeelGreatProduct(baStatus), FeelGreatAPI.GetSubscriptionConfig()])

            StoreLoader.InitAfterLoadSetting(settingResponse.data, subscriptionConfigResponse.data)

            runInAction(() => {
                // StoreCheckout.dataReady = true
                StoreLoader.SetApiLoaded('settings', true)
            })
        }
    }
}

export const SetData = async () => {
    if (StoreProducts.GetIsInit()) {
        if (StoreCheckout.GetSelectedItem() === '') {
            StoreCheckout.SetSelectedItem(StoreFeelGreat.defaultValues.flavor)
        }

        if (StoreCheckout.GetSelectedPurchaseOption() === '') {
            StoreCheckout.SetSelectedPurchaseOption(StoreFeelGreat.defaultValues.purchaseOption)
        }
    }

    const detail_page = StoreFormat.GetDetailPage()
    let formData = StoreFeelGreat.GetCheckoutForms()
    const formKeyMap = formKeyByFormatKey(StoreCheckout.CheckOutInstance().Instance())
    Object.keys(formData).map(key => {
        applySessionToForm(get(formData, [key, 'inputs'], []), detail_page, formKeyMap)
        return true
    })

    // The dialling code is form state too, it just lives beside the inputs instead of in them. Without
    // this the phone widget falls back to the market's own code whenever it renders before the session
    // lands — a Bahrain order came back showing +971.
    if (get(detail_page, 'country_calling_code')) {
        StoreCheckout.SetPhoneNumberCode(detail_page.country_calling_code)
    }

    StoreFeelGreat.Init(formData)
    StoreCheckout.Init(formData)

    if (get(detail_page, 'ship_address_options', false)) {
        runInAction(() => (StoreCheckout.useBillingAddressSameAsShipping = false))
        Object.keys(detail_page).map(key => {
            if (/^ship_address+?/.test(key)) {
                let newKey = key.replace('ship_address_', '')
                StoreCheckout.SetBillingAddressForm(newKey, detail_page[key])
            }
        })
    }

    await StoreCheckout.RestoreCart(parseShipmentPacks(detail_page.shipment_packs))
    if (/payment-response/.test(window.location.href)) {
        runInAction(() => {
            StoreLoader.SetApiLoaded('settings', true)
        })
    }
}

export const GetCustomer = async (customerId = null) => {
    const response = await Customers.GetCustomer(customerId)
    const item = get(response.data, 'items[0]')

    if (item) {
        let baStatus = StoreUser.CustomerType(item.type, item.status).short || null
        await StoreReferral.SetReferralData(item, baStatus)
        // The referral checkout prices off its own store, which the ReferralBar fills on the landing
        // page — a reload straight into checkout never passed through it.
        await StoreReferralUae.RestoreFromReferralId(customerId)
        const settingResponse = await GetFeelGreatProduct(baStatus)
        StoreLoader.InitAfterLoadSetting(settingResponse.data)
        StoreCheckout.SetUseReferralPrice(true)
    }
}

export const GetUuid = async (uuid, countryCode3Alpha, calc) => {
    try {
        if (uuid && countryCode3Alpha) {
            StoreLoader.SetLoadFormatApi(true)
            const response = await ReferralUaeAPIs.getHotFormatV2(uuid, countryCode3Alpha, calc)
            const loginId = get(response.data, 'ushop.detail_page.login_id')
            const referralId = get(response.data, 'ushop.detail_page.referral_id')

            if (loginId) {
                const token = get(response.data, 'ushop.detail_page.token')
                // const userResponse = await userAPI.getMenuWithLogin(loginId, countryCode3Alpha, token)
                // StoreAuth.initUser(userResponse.data, token)
                // await GetFeelGreatProduct(response.data.userStatus)
                await StoreAuth.getCustomerMenu(loginId, countryCode3Alpha, token)
            } else {
                if (referralId) {
                    await GetCustomer(referralId)
                } else {
                    const settingResponse = await GetFeelGreatProduct()
                    StoreLoader.InitAfterLoadSetting(settingResponse.data)
                }
            }
            StoreCheckout.uuid = uuid
            await StoreFormat.SetData(response.data)
        }
    } catch (error) {
        // Swallowed on purpose: the caller lowers the format-API flag in its finally, and the page has
        // to come up with whatever the session gave us rather than never finishing loading.
        console.error('Initialize.GetUuid failed', error)
    }
}
